import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { ModelCatalogEntry, ModelRef } from "../lib/types.js";
import { SPEED_LABEL, COST_LABEL } from "./catalog.js";

/**
 * Persistent chat model dropdown (no modal). Lists the models available for
 * general chat with a brief explanation, and reports the choice upward. The
 * selection persists via the parent (localStorage) until the user changes it.
 */
export function AIModelSelect({
  projectId,
  value,
  onChange
}: {
  projectId: string;
  value: ModelRef | null;
  onChange: (ref: ModelRef) => void;
}) {
  const [models, setModels] = useState<ModelCatalogEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.aiModels(projectId, "general-chat")
      .then((r) => {
        if (cancelled) return;
        const usable = r.models.filter((m) => m.available);
        setModels(usable);
        // If nothing chosen yet (or the stored choice is no longer available),
        // default to the recommended model.
        const current = value && usable.find((m) => m.provider === value.provider && m.model === value.model);
        if (!current) {
          const rec = usable.find((m) => m.recommended) ?? usable[0];
          if (rec) onChange({ provider: rec.provider, model: rec.model });
        }
      })
      .catch(() => !cancelled && setModels([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (models.length === 0) return null;
  const selectedKey = value ? `${value.provider}:${value.model}` : "";
  const selected = models.find((m) => `${m.provider}:${m.model}` === selectedKey);

  return (
    <div className="field" style={{ margin: 0 }}>
      <select
        className="select"
        aria-label="Chat model"
        value={selectedKey}
        onChange={(e) => {
          const m = models.find((x) => `${x.provider}:${x.model}` === e.target.value);
          if (m) onChange({ provider: m.provider, model: m.model });
        }}
        style={{ fontSize: 12, padding: "3px 6px" }}
      >
        {models.map((m) => (
          <option key={`${m.provider}:${m.model}`} value={`${m.provider}:${m.model}`}>
            {m.displayName} ({SPEED_LABEL[m.speed]})
          </option>
        ))}
      </select>
      {selected && (
        <p className="faint" style={{ fontSize: 10.5, margin: "3px 0 0" }}>
          {SPEED_LABEL[selected.speed]} · {COST_LABEL[selected.cost]} — {selected.description}
        </p>
      )}
    </div>
  );
}
